import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import type { MeResponse } from "@starsach/shared-types";
import { useAuth } from "../auth/AuthProvider";
import { StepUpProvider } from "../auth/StepUpProvider";
import { PoweredBy, StarsachWordmark } from "../components/Brand";
import { NudgeBanner } from "../components/NudgeBanner";
import { Dialog, btnPrimary, btnSecondary, sentence, vtag } from "../components/ui";
import { setViewAsMode } from "../viewAs";

// tableau.com-style navigation: quiet text, the active item underlined in the accent.
const linkClass = ({ isActive }: { isActive: boolean }) =>
  `-mb-px border-b-2 px-3 py-4 text-sm font-medium transition-colors ${isActive ? "border-accent text-accent-strong dark:border-accent dark:text-white" : "border-transparent text-neutral-600 hover:border-neutral-300 hover:text-ink dark:text-neutral-400 dark:hover:text-white"}`;

/**
 * ADR-0011: the one thing a view-as tab shows that the customer's own tab
 * does not. Everything below it is the customer's portal, served by the
 * customer's own routes; the banner names whose, and whether it can be
 * changed from here. A mode change reloads the tab so nothing cached
 * under the old mode survives.
 */
function ViewAsBanner({ viewAs }: { viewAs: NonNullable<MeResponse["viewAs"]> }) {
  const [confirming, setConfirming] = useState(false);
  const name = viewAs.organisationName ?? viewAs.organisationId;
  const switchTo = (mode: "read" | "write") => {
    setViewAsMode(viewAs.organisationId, mode);
    window.location.reload();
  };
  return (
    <div role="region" aria-label="Viewing a customer's portal" className="border-b border-brand-text/30 bg-brand/10 text-sm text-neutral-900 dark:text-neutral-100">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
        <span>
          Viewing <strong>{name}</strong>'s portal as Gairm staff ·{" "}
          {viewAs.mode === "write" ? <strong className="text-brand-text">editing enabled</strong> : <span>read-only</span>}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {viewAs.mode === "write" ? (
            <button type="button" onClick={() => switchTo("read")} className={`${btnSecondary} min-h-8 px-3 text-xs`}>
              Back to read-only
            </button>
          ) : (
            <button type="button" onClick={() => setConfirming(true)} className={`${btnSecondary} min-h-8 px-3 text-xs`}>
              Enable editing
            </button>
          )}
          <a href="/customers" className="text-xs underline hover:text-neutral-700 dark:hover:text-neutral-300">
            Exit
          </a>
        </span>
      </div>
      {confirming && (
        <Dialog title={`Edit ${name}'s portal?`} onClose={() => setConfirming(false)}>
          <p className="text-sm">
            You will be able to change anything their own manager could. Every change is recorded in their audit log under your name, and they
            can see it. Nothing happens until you save something.
          </p>
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={() => switchTo("write")} className={btnPrimary}>
              Enable editing
            </button>
            <button type="button" onClick={() => setConfirming(false)} className={btnSecondary}>
              Cancel
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

export function AppLayout() {
  const { me, signOut } = useAuth();
  // Root package.json version, injected by vite.config.ts; the API serves its own on /v1/me.
  const uiVersion = import.meta.env.VITE_PORTAL_VERSION ?? "dev";
  return (
    <StepUpProvider>
    <div className="min-h-screen bg-canvas text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:rounded focus:bg-white focus:p-2">
        Skip to content
      </a>
      {me?.viewAs && <ViewAsBanner viewAs={me.viewAs} />}
      <header className="border-b border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4">
          <div className="flex items-center gap-8">
            <StarsachWordmark className="text-lg text-ink dark:text-white" />
            <nav aria-label="Main">
              <ul className="flex items-center">
                <li><NavLink to="/" end className={linkClass}>Focus</NavLink></li>
                <li><NavLink to="/customers" className={linkClass}>Customers</NavLink></li>
                <li><NavLink to="/trusted-advisor" className={linkClass}>Trusted Advisor</NavLink></li>
                {/* ADR-0013: a tenant's own people; staff reach it through view-as. */}
                {me?.role === "customer" && <li><NavLink to="/users" className={linkClass}>Users</NavLink></li>}
                {/* Cross-tenant by definition (requirements.md section 12). */}
                {me?.role === "internal-team" && <li><NavLink to="/platform" className={linkClass}>Platform</NavLink></li>}
                <li><NavLink to="/docs" className={linkClass}>Docs</NavLink></li>
              </ul>
            </nav>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {/* Section 13: the identity is the way into Account. */}
            <Link to="/account" className="text-neutral-600 hover:text-ink hover:underline dark:text-neutral-400 dark:hover:text-white" aria-label="Account">
              {me?.displayName ?? me?.email} · <span className="font-medium">{me?.viewAs ? "Gairm staff" : me?.role && sentence(me.role)}</span>
            </Link>
            <button type="button" onClick={() => void signOut()} className="rounded-md border border-neutral-300 px-3 py-1.5 font-medium text-ink hover:bg-neutral-100 dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-800">
              Sign out
            </button>
          </div>
        </div>
      </header>
      <NudgeBanner />
      <main id="main" className="mx-auto max-w-7xl px-4 py-8">
        <Outlet />
      </main>
      <footer className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-4 text-xs text-neutral-500 dark:text-neutral-400">
        <span>
          Starsach Portal {vtag(uiVersion)}
          {me?.portalVersion && me.portalVersion !== uiVersion ? ` (API ${vtag(me.portalVersion)})` : ""}
          {" · "}
          <Link to={`/docs/release-notes#${vtag(uiVersion)}`} className="text-accent-strong underline hover:text-accent dark:text-accent">
            Release notes
          </Link>
          {" · "}
          <Link to="/docs/what-leaves-your-account" className="text-accent-strong underline hover:text-accent dark:text-accent">
            What leaves your account
          </Link>
          {" · "}
          <Link to="/docs" className="text-accent-strong underline hover:text-accent dark:text-accent">
            Documentation
          </Link>
        </span>
        <PoweredBy />
      </footer>
    </div>
    </StepUpProvider>
  );
}
